// The Qtag-scan overlay's one drawing (TASK-217.50, decision-45's "one anatomy
// everywhere" applied to the QR scanner).
//
// Five copies of this markup used to drift independently — two in ERB
// (submissions/_editable_field.html.erb) and three built as raw strings by
// form_preview_controller.js / submission_wizard_controller.js. One of the JS
// copies (form_preview's product-installer field) had silently lost its
// modal markup altogether, so its "Scan" button threw a missing-target error.
// (form_preview_controller.js is gone since TASK-267.4 — the builder's Preview
// now runs the submission wizard itself.)
//
// This is the JS side of that single drawing: it hand-builds a native
// <dialog> with the exact chrome classes Common::Dialog::Component renders
// (modal / modal-backdrop / modal-content / modal-header / modal-body /
// modal-close from components/modal.scss) because a ViewComponent can't be
// rendered from client JS. The ERB call sites render the real
// Common::Dialog::Component instead — see submissions/_editable_field.html.erb.
//
// The dialog's own dismissal (Escape, backdrop, the header close button) is
// owned entirely by dialog_controller.js, unmodified. qr_scanner_controller.js
// only opens/closes it through a Stimulus outlet and owns the camera — two
// controllers nested together, not merged into one.

function esc(value) {
  const div = document.createElement("div")
  div.textContent = value == null ? "" : String(value)
  return div.innerHTML
}

const QR_CODE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" aria-hidden="true" class="h-4 w-4 flex-shrink-0">
  <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 013.75 9.375v-4.5zM3.75 14.625c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5a1.125 1.125 0 01-1.125-1.125v-4.5zM13.5 4.875c0-.621.504-1.125 1.125-1.125h4.5c.621 0 1.125.504 1.125 1.125v4.5c0 .621-.504 1.125-1.125 1.125h-4.5A1.125 1.125 0 0113.5 9.375v-4.5z"/>
  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 6.75h.75v.75h-.75v-.75zM6.75 16.5h.75v.75h-.75v-.75zM16.5 6.75h.75v.75h-.75v-.75zM13.5 13.5h.75v.75h-.75v-.75zM13.5 19.5h.75v.75h-.75v-.75zM19.5 13.5h.75v.75h-.75v-.75zM19.5 19.5h.75v.75h-.75v-.75zM16.5 16.5h.75v.75h-.75v-.75z"/>
</svg>`

const X_MARK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" class="h-5 w-5">
  <path fill-rule="evenodd" d="M5.47 5.47a.75.75 0 011.06 0L12 10.94l5.47-5.47a.75.75 0 111.06 1.06L13.06 12l5.47 5.47a.75.75 0 11-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 01-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 010-1.06z" clip-rule="evenodd"/>
</svg>`

let nextDialogId = 0

// options:
//   value           current input value
//   required        marks the field required (server-validated; no HTML5 attr)
//   label           label text — omit when the caller already renders one
//   wrapperClass    extra classes on the outermost element
//   inputAttr       extra raw attribute string(s) for the <input> (e.g. `data-pi-qtag`)
//   inputDataAction extra data-action value merged onto the <input>
export function renderQrScannerField({
  value = "",
  required = false,
  label = null,
  wrapperClass = "",
  inputAttr = "",
  inputDataAction = "",
} = {}) {
  const dialogId = `qr-scanner-dialog-${(nextDialogId += 1)}`
  const labelHtml = label
    ? `<label class="label">${esc(label)}${required ? '<span class="text-red-500 ml-1">*</span>' : ""}</label>`
    : ""
  const actionAttr = inputDataAction ? ` data-action="${esc(inputDataAction)}"` : ""

  return `
    <div class="${wrapperClass}" data-controller="qr-scanner" data-qr-scanner-dialog-outlet="#${dialogId}" data-action="dialog:closed->qr-scanner#stopScanning">
      ${labelHtml}
      <div class="flex gap-2">
        <input type="text" class="form-control" value="${esc(value)}" placeholder="e.g. A000001"
               data-qr-scanner-target="input"${actionAttr} ${inputAttr}>
        <button type="button" class="btn-outline-gray-sm inline-flex items-center gap-1.5 shrink-0"
                data-action="click->qr-scanner#start" title="Scan Qtag">
          ${QR_CODE_ICON}
        </button>
      </div>
      <dialog id="${dialogId}" class="modal p-0" data-controller="dialog" data-dialog-target="dialog" data-action="cancel->dialog#cancel">
        <div class="modal-backdrop" data-dialog-target="backdrop" data-action="click->dialog#backdropClose">
          <div class="modal-content modal-sm">
            <div class="modal-header">
              <h3>Scan Qtag</h3>
              <button type="button" class="modal-close" data-action="click->dialog#cancel" aria-label="Close">
                ${X_MARK_ICON}
              </button>
            </div>
            <div class="modal-body p-4">
              <video data-qr-scanner-target="video" class="w-full rounded-lg"></video>
              <p data-qr-scanner-target="errorMessage" class="hidden text-sm text-red-600 mt-2"></p>
            </div>
          </div>
        </div>
      </dialog>
    </div>
  `
}
